import { useEffect, useRef } from "react";

import { useQuery } from "@tanstack/react-query";
import { renderInlineBold } from "@recruiter/shared";

import { SellerQueryError, threadDetailQueryOptions } from "../../lib/queries/seller";
import { useSellerStore } from "../../lib/store/seller-store";
import { sellerDocUrl } from "../../lib/seller-client";
import { BriefCard } from "./brief-card";
import { DocExportButton } from "./doc-export-button";
import { OfferStatus } from "./offer-status";
import { ReplyInput } from "./reply-input";
import { SellerErrorState } from "./seller-error-state";

// One open thread's chat. Server state via TanStack Query (keyed by talent +
// thread): opening a thread fetches it; a `seller.message` event is appended to
// this cache entry by the dispatcher (no re-GET), and a `seller.thread.update`
// invalidates it so the authoritative offer re-loads. Switching threads re-keys
// the query, so the loading state shows automatically.
export function ThreadView({ threadId }: { threadId: string }) {
  const sellerId = useSellerStore((s) => s.activeSellerId);
  const {
    data: thread,
    isPending,
    error,
    refetch,
  } = useQuery(threadDetailQueryOptions(sellerId, threadId));
  const bottom = useRef<HTMLDivElement | null>(null);

  // Scroll to the newest message — fires when the thread loads and whenever the
  // dispatcher appends a pushed message (the cached messages array grows).
  const messageCount = thread?.messages.length ?? 0;
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messageCount]);

  if (isPending) {
    return (
      <div className="sl-center">
        <div className="sl-empty">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <SellerErrorState
        title="Couldn't load thread"
        message={error instanceof SellerQueryError ? error.message : "Something went wrong."}
        onRetry={() => void refetch()}
      />
    );
  }
  if (!thread) return null;

  return (
    <div className="sl-chat">
      <div className="sl-topbar">
        <div>
          <div className="sl-topbar__title">{thread.client_name}</div>
          <div className="sl-topbar__sub">{thread.subject}</div>
        </div>
      </div>
      <div className="sl-chat__scroll">
        <BriefCard thread={thread} />
        {thread.messages.map((m) => {
          const mine = m.sender === "seller";
          const docs = m.documents ?? [];
          const attachments = m.attachments ?? [];
          return (
            <div key={m.id} className={`sl-msg ${mine ? "sl-msg--seller" : "sl-msg--sterling"}`}>
              <div className="sl-msg__who">{mine ? "You" : "Concierge · Sterling"}</div>
              <div className="sl-msg__body">{renderInlineBold(m.body)}</div>
              {docs.length > 0 ? (
                <div className="sl-msg__docs">
                  {docs.map((d) => (
                    <span key={d.kind} className="sl-doc">
                      <a
                        className="sl-doc__view"
                        href={sellerDocUrl(d.view_url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        📄 {d.label}
                      </a>
                      <DocExportButton
                        threadId={threadId}
                        kind={d.kind}
                        sellerId={sellerId}
                        exportUrl={d.export_url}
                      />
                    </span>
                  ))}
                </div>
              ) : null}
              {attachments.length > 0 ? (
                <div className="sl-msg__docs">
                  {attachments.map((a) => {
                    const scanning = a.scan_status !== "clean";
                    const note = scanning
                      ? "scanning…"
                      : a.status === "failed"
                        ? "couldn't read"
                        : a.status !== "ready"
                          ? "reading…"
                          : null;
                    return (
                      <span key={a.id} className="sl-doc">
                        {a.scan_status === "clean" ? (
                          <a
                            className="sl-doc__view"
                            href={sellerDocUrl(a.content_url)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            📎 {a.file_name}
                          </a>
                        ) : (
                          <span className="sl-doc__view">📎 {a.file_name}</span>
                        )}
                        {note ? (
                          <span className="sl-doc__pdf" aria-hidden="true">
                            {note}
                          </span>
                        ) : null}
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
        <OfferStatus offer={thread.offer ?? null} />
        <div ref={bottom} />
      </div>
      {/* key={threadId}: a fresh composer (cleared draft) per thread. */}
      <ReplyInput key={threadId} threadId={threadId} sellerId={sellerId} />
    </div>
  );
}
